import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  Contact,
  ContactBody,
  ContactWithBusiness,
  GetResponse,
} from './types';

export const getAllContacts = (dispatch: Dispatch) =>
  useQuery({
    queryKey: ['contacts', 'all'],
    queryFn: async (): Promise<
      GetResponse<ContactWithBusiness> | undefined
    > => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/contact`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    retry: 1,
  });

export const getBusinessGlobalContacts = (
  dispatch: Dispatch,
  businessIdentifier?: string,
) =>
  useQuery({
    queryKey: ['contacts', 'global', businessIdentifier],
    queryFn: async (): Promise<GetResponse<Contact> | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/contact/global/${businessIdentifier}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    retry: 1,
    enabled: !!businessIdentifier,
  });

export const getBusinessContacts = (
  dispatch: Dispatch,
  businessIdentifier?: string,
  query?: string,
) =>
  useQuery({
    queryKey: ['contacts', 'business', businessIdentifier, query],
    queryFn: async (): Promise<GetResponse<Contact> | undefined> => {
      const queryString = query ? `?${query}` : '';
      const response = await requestHandler({
        method: 'get',
        url: `internal/contact/business/${businessIdentifier}${queryString}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    retry: 1,
    enabled: !!businessIdentifier,
  });

export const createBusinessContact = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['contact', 'create'],
    mutationFn: async (data: ContactBody) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/contact`,
        dispatch,
        data,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      showAlert(dispatch, 'success', 'Contacto creado con éxito');
      await queryClient.invalidateQueries({
        queryKey: ['contacts'],
      });
    },
  });
};

export const updateBusinessContact = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['contact', 'update'],
    mutationFn: async (data: ContactBody & { id: number }) => {
      const { id, ...body } = data;
      const response = await requestHandler({
        method: 'patch',
        url: `internal/contact/${id}`,
        dispatch,
        data: body,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contacts'],
      });
      showAlert(dispatch, 'success', 'Contacto actualizado con éxito');
    },
  });
};

export const selectContact = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['contact', 'select'],
    mutationFn: async (id: number) => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/contact/select/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      showAlert(dispatch, 'success', 'Contacto seleccionado con éxito');
      await queryClient.invalidateQueries({
        queryKey: ['contacts'],
      });
    },
  });
};

export const unSelectContact = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['contact', 'unSelect'],
    mutationFn: async (id: number) => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/contact/unselect/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contacts'],
      });
      showAlert(dispatch, 'success', 'Contacto desmarcado con éxito');
    },
  });
};

export const selectContactInvoicing = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['contact', 'selectInvoicing'],
    mutationFn: async (id: number) => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/contact/select/invoicing/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contacts'],
      });
      showAlert(
        dispatch,
        'success',
        'Contacto seleccionado como contacto de facturación con éxito',
      );
    },
  });
};

export const unSelectContactInvoicing = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['contact', 'unSelectInvoicing'],
    mutationFn: async (id: number) => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/contact/unselect/invoicing/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contacts'],
      });
      showAlert(
        dispatch,
        'success',
        'Contacto eliminado como contacto de facturación con éxito',
      );
    },
  });
};

export const deleteBusinessContact = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['contact', 'delete'],
    mutationFn: async (id: number) => {
      const response = await requestHandler({
        method: 'delete',
        url: `internal/contact/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async () => {
      showAlert(dispatch, 'success', 'Contacto eliminado con éxito');
      await queryClient.invalidateQueries({
        queryKey: ['contacts'],
      });
    },
  });
};
